import { useState, useRef, useEffect, useMemo } from 'react';
import { Calculator, Sparkles, ChevronDown, ChevronUp, Search, Loader2, Camera, X, Image as ImageIcon, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import ImplantExportDialog from './ImplantExportDialog';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const PROFILE_LABELS = {
  low: 'Bajo', moderate: 'Moderado', moderate_plus: 'Moderado Plus',
  high: 'Alto', ultra_high: 'Ultra Alto', extra_high: 'Extra Alto',
};
const PROFILE_COLORS = {
  low: 'bg-brand-soft/60 text-brand-navy', moderate: 'bg-green-100 text-green-700',
  moderate_plus: 'bg-yellow-100 text-yellow-700', high: 'bg-orange-100 text-orange-700',
  ultra_high: 'bg-red-100 text-red-700', extra_high: 'bg-brand-deep/20 text-brand-deep',
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ImplantCalculator({
  measurements,
  referencePhotos = [],
  patientId,
  patientName,
  defaultOpen = false,
  variant = 'compact',
  // Optional: wire into ClinicalNoteForm so the current measurements +
  // recommendation can be persisted into the clinical note and optionally
  // printed in the medical report.
  noteSnapshot, // { snapshot, include_in_report }
  onNoteSnapshotChange, // ({ snapshot, include_in_report }) => void
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [extraPhotos, setExtraPhotos] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]); // photos auto-loaded from MedicalGallery
  const [selectedGalleryIds, setSelectedGalleryIds] = useState(new Set()); // user toggles
  const [galleryLoading, setGalleryLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Auto-load patient photos from MedicalGallery (medical_files) when patientId is provided
  useEffect(() => {
    if (!patientId || !open) return;
    let abort = false;
    setGalleryLoading(true);
    api.get(`/medical-files/patient/${patientId}`)
      .then(({ data }) => {
        if (abort) return;
        // Keep only image files (frontal/lateral/oblicua/etc preoperative photos)
        const imgs = (data || []).filter(f => {
          const ct = (f.content_type || f.mime_type || '').toLowerCase();
          const fn = (f.original_filename || f.filename || '').toLowerCase();
          return ct.startsWith('image/') || /\.(jpg|jpeg|png|webp|heic|heif)$/i.test(fn);
        });
        setGalleryPhotos(imgs);
        // Pre-select first 4 (most common preop frontal/lateral set)
        setSelectedGalleryIds(new Set(imgs.slice(0, 4).map(p => p.id)));
      })
      .catch(() => { /* silent — calculator works without these */ })
      .finally(() => { if (!abort) setGalleryLoading(false); });
    return () => { abort = true; };
  }, [patientId, open]);

  const toggleGalleryPhoto = (id) => {
    setSelectedGalleryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  
  const [form, setForm] = useState({
    base_width_mm: measurements?.base_width || '',
    height_mm: measurements?.height || '',
    chest_circumference_cm: measurements?.chest_circumference || '',
    procedure_type: 'augmentation',
    implant_type_preference: 'both',
    brand_preference: '',
    product_line_preference: '',
    profile_preference: '',
    surface_preference: '',
    additional_notes: '',
  });

  // ────────────────────────────────────────────────────────────────────────
  // Draft persistence (scoped per-patient, 24h TTL)
  // ────────────────────────────────────────────────────────────────────────
  // Persists the in-flight implant order so the user can navigate away
  // (e.g. to look up patient history) and come back without losing form
  // inputs, calculation results, or photo selections.
  //
  // What we persist:
  //   • `form`: all the calculator inputs (volume, base, brand prefs, …)
  //   • `result`: the AI recommendation payload returned by the backend
  //   • `selectedGalleryIds`: which preop photos the user toggled
  //
  // What we do NOT persist:
  //   • `extraPhotos`: contains base64 of large File uploads (re-upload
  //     is faster than risking a localStorage quota error).
  //   • `galleryPhotos`: re-fetched from the API on mount; cheap.
  //
  // Hydration policy: only restore a draft saved <24h ago, scoped to the
  // *same* patient. This prevents stale drafts from leaking across
  // patients or persisting for weeks (drafts that old are almost always
  // unwanted noise).
  const draftStorageKey = `implant_draft_${patientId || 'global'}`;
  const draftHydratedRef = useRef(false);
  const [draftRestored, setDraftRestored] = useState(false);

  // One-shot hydration from localStorage on first render (after defaults
  // were initialised). Runs synchronously inside an effect so all states
  // are already mounted.
  useEffect(() => {
    if (draftHydratedRef.current) return;
    draftHydratedRef.current = true;
    try {
      const raw = localStorage.getItem(draftStorageKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft || !draft.savedAt) return;
      if (Date.now() - draft.savedAt > 24 * 3600 * 1000) {
        localStorage.removeItem(draftStorageKey);
        return;
      }
      if (draft.form) {
        // Merge: keep the measurements props (caller-provided ground truth)
        // when they are non-empty, otherwise use the draft.
        setForm(prev => ({
          ...prev,
          ...draft.form,
          base_width_mm: measurements?.base_width || draft.form.base_width_mm || prev.base_width_mm,
          height_mm: measurements?.height || draft.form.height_mm || prev.height_mm,
          chest_circumference_cm: measurements?.chest_circumference || draft.form.chest_circumference_cm || prev.chest_circumference_cm,
        }));
      }
      if (draft.result) setResult(draft.result);
      if (Array.isArray(draft.selectedGalleryIds)) {
        // Restore only ids that still exist in gallery (handled by toggle)
        setSelectedGalleryIds(new Set(draft.selectedGalleryIds));
      }
      if (typeof draft.open === 'boolean') setOpen(draft.open);
      setDraftRestored(true);
    } catch {
      /* malformed draft — silently ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on any meaningful change. Throttled implicitly by React's
  // batching; the payload is small enough that a debounce isn't worth it.
  useEffect(() => {
    if (!draftHydratedRef.current) return; // wait until hydration ran
    const hasContent = (
      (form && (form.base_width_mm || form.height_mm || form.additional_notes)) ||
      result ||
      selectedGalleryIds.size > 0
    );
    try {
      if (!hasContent) {
        localStorage.removeItem(draftStorageKey);
        return;
      }
      localStorage.setItem(draftStorageKey, JSON.stringify({
        savedAt: Date.now(),
        patientId: patientId || null,
        open,
        form,
        result,
        selectedGalleryIds: Array.from(selectedGalleryIds),
      }));
    } catch {
      /* quota exceeded — drop silently so the page keeps working */
    }
  }, [form, result, selectedGalleryIds, open, patientId, draftStorageKey]);

  // Discard the draft (used by the "Empezar de nuevo" button and after
  // a successful PDF export).
  const clearDraft = () => {
    try { localStorage.removeItem(draftStorageKey); } catch { /* noop */ }
    setForm({
      base_width_mm: measurements?.base_width || '',
      height_mm: measurements?.height || '',
      chest_circumference_cm: measurements?.chest_circumference || '',
      procedure_type: 'augmentation',
      implant_type_preference: 'both',
      brand_preference: '',
      product_line_preference: '',
      profile_preference: '',
      surface_preference: '',
      additional_notes: '',
    });
    setResult(null);
    setExtraPhotos([]);
    setSelectedGalleryIds(new Set());
    setDraftRestored(false);
  };

  // Dynamic catalog: maps brand → list of product_lines, fetched once.
  // The two dropdowns ("Marca" and "Modelo / Línea") build from this.
  const [catalogBrands, setCatalogBrands] = useState(null);
  useEffect(() => {
    if (!open || catalogBrands) return;
    let abort = false;
    api.get('/implant-catalog/brands')
      .then(({ data }) => { if (!abort) setCatalogBrands(data || {}); })
      .catch(() => { if (!abort) setCatalogBrands({}); });
    return () => { abort = true; };
  }, [open, catalogBrands]);

  const brandList = useMemo(
    () => catalogBrands ? Object.keys(catalogBrands).sort() : [],
    [catalogBrands]
  );
  // Product lines for the currently-selected brand (deduplicated, sorted)
  const productLineList = useMemo(() => {
    if (!form.brand_preference || form.brand_preference === 'all_brands') return [];
    const lines = catalogBrands?.[form.brand_preference] || [];
    const unique = Array.from(new Set(lines.map(l => l.product_line).filter(Boolean)));
    return unique.sort();
  }, [catalogBrands, form.brand_preference]);

  const updateForm = (field, value) => setForm(prev => {
    const next = { ...prev, [field]: value };
    // Reset product_line when brand changes — otherwise the user could
    // end up with an inconsistent (brand, product_line) pair.
    if (field === 'brand_preference' && prev.brand_preference !== value) {
      next.product_line_preference = '';
    }
    return next;
  });

  // Live "Top 3" preview: debounced 400ms, no AI/no photos.
  // Persisted to localStorage scoped per-patient so the panel shows up
  // immediately on remount before the first fetch resolves.
  const previewStorageKey = `implant_preview_${patientId || 'global'}`;
  const [preview, setPreview] = useState(() => {
    try {
      const raw = localStorage.getItem(previewStorageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Expire after 7 days to avoid showing stale recommendations
      if (parsed?.savedAt && Date.now() - parsed.savedAt > 7 * 24 * 3600 * 1000) {
        return null;
      }
      return parsed?.data || null;
    } catch {
      return null;
    }
  });
  const [previewLoading, setPreviewLoading] = useState(false);
  useEffect(() => {
    const bw = parseFloat(form.base_width_mm);
    if (!bw || bw < 70 || bw > 200) {
      setPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await api.post('/implant-calculator/quick-preview', {
          base_width_mm: bw,
          height_mm: form.height_mm ? parseFloat(form.height_mm) : null,
          chest_circumference_cm: form.chest_circumference_cm ? parseFloat(form.chest_circumference_cm) : null,
          procedure_type: form.procedure_type,
          implant_type_preference: form.implant_type_preference,
          brand_preference: form.brand_preference && form.brand_preference !== 'all_brands' ? form.brand_preference : null,
          product_line_preference: form.product_line_preference && form.product_line_preference !== 'all_lines' ? form.product_line_preference : null,
          profile_preference: form.profile_preference || null,
          surface_preference: form.surface_preference && form.surface_preference !== 'any_surface' ? form.surface_preference : null,
        }, { timeout: 8000 });
        setPreview(data);
        try {
          localStorage.setItem(previewStorageKey, JSON.stringify({ data, savedAt: Date.now() }));
        } catch { /* quota exceeded, ignore */ }
      } catch {
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.base_width_mm, form.height_mm, form.chest_circumference_cm,
      form.procedure_type, form.implant_type_preference, form.brand_preference,
      form.product_line_preference,
      form.profile_preference, form.surface_preference, previewStorageKey]);

  const handleAddPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      const base64 = await fileToBase64(file);
      setExtraPhotos(prev => [...prev, { name: file.name, base64, preview: URL.createObjectURL(file) }]);
    }
    e.target.value = '';
  };

  const removeExtraPhoto = (idx) => {
    setExtraPhotos(prev => {
      URL.revokeObjectURL(prev[idx]?.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleCalculate = async () => {
    if (!form.base_width_mm) {
      toast.error('Introduce el ancho de base mamaria');
      return;
    }

    setLoading(true);
    try {
      const body = {
        ...form,
        base_width_mm: parseFloat(form.base_width_mm),
        height_mm: form.height_mm ? parseFloat(form.height_mm) : null,
        chest_circumference_cm: form.chest_circumference_cm ? parseFloat(form.chest_circumference_cm) : null,
        brand_preference: form.brand_preference && form.brand_preference !== 'all_brands' ? form.brand_preference : null,
        product_line_preference: form.product_line_preference && form.product_line_preference !== 'all_lines' ? form.product_line_preference : null,
        profile_preference: form.profile_preference || null,
        surface_preference: form.surface_preference && form.surface_preference !== 'any_surface' ? form.surface_preference : null,
        additional_notes: form.additional_notes || null,
        reference_photo_ids: [
          ...referencePhotos.map(p => p.id).filter(Boolean),
          ...galleryPhotos.filter(p => selectedGalleryIds.has(p.id)).map(p => p.id),
        ],
        extra_photos_base64: extraPhotos.map(p => p.base64),
      };
      
      const { data } = await api.post('/implant-calculator/recommend', body, { timeout: 90000 });
      setResult(data);
    } catch (err) {
      toast.error('Error al calcular recomendación');
    } finally {
      setLoading(false);
    }
  };

  const totalPhotos = referencePhotos.length + extraPhotos.length + selectedGalleryIds.size;

  return (
    <div className="border border-brand-medium rounded-lg bg-brand-soft/40/30 overflow-hidden" data-testid="implant-calculator">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-brand-soft/40/60 transition-colors"
        onClick={() => setOpen(!open)}
        data-testid="implant-calculator-toggle"
      >
        <div className="flex items-center gap-2">
          <Calculator size={18} className="text-brand-navy" />
          <span className="font-medium text-sm text-brand-navy">Calculadora de Prótesis</span>
          <Badge className="bg-brand-soft/40 text-brand-navy text-[10px]">IA + Catálogo + Visión</Badge>
          {draftRestored && (
            <Badge
              className="bg-brand-soft text-brand-deep border-brand-medium text-[10px] gap-1"
              data-testid="implant-draft-restored-badge"
              title="Se ha recuperado el borrador guardado de tu sesión anterior"
            >
              Borrador recuperado
            </Badge>
          )}
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4">
          {/* Input form */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-medium">Ancho base mamaria (mm) *</Label>
              <Input type="number" placeholder="Ej: 120" value={form.base_width_mm}
                onChange={e => updateForm('base_width_mm', e.target.value)} className="h-9 text-sm"
                data-testid="implant-base-width" />
            </div>
            <div>
              <Label className="text-xs font-medium">Altura mamaria (mm)</Label>
              <Input type="number" placeholder="Ej: 140" value={form.height_mm}
                onChange={e => updateForm('height_mm', e.target.value)} className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs font-medium">Contorno torácico (cm)</Label>
              <Input type="number" placeholder="Ej: 86" value={form.chest_circumference_cm}
                onChange={e => updateForm('chest_circumference_cm', e.target.value)} className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs font-medium">Procedimiento</Label>
              <Select value={form.procedure_type} onValueChange={v => updateForm('procedure_type', v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="augmentation">Aumento</SelectItem>
                  <SelectItem value="reconstruction">Reconstrucción</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Forma</Label>
              <Select value={form.implant_type_preference} onValueChange={v => updateForm('implant_type_preference', v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Ambas</SelectItem>
                  <SelectItem value="round">Redonda</SelectItem>
                  <SelectItem value="anatomical">Anatómica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Marca</Label>
              <Select value={form.brand_preference} onValueChange={v => updateForm('brand_preference', v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="implant-brand-select"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_brands">Todas</SelectItem>
                  {brandList.length === 0 ? (
                    /* Fallback while catalog is loading */
                    <>
                      <SelectItem value="Mentor">Mentor</SelectItem>
                      <SelectItem value="Polytech">Polytech</SelectItem>
                      <SelectItem value="Polytech B-Lite">Polytech B-Lite</SelectItem>
                      <SelectItem value="Silimed">Silimed</SelectItem>
                      <SelectItem value="Motiva">Motiva</SelectItem>
                    </>
                  ) : (
                    brandList.map(b => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Acabado</Label>
              <Select value={form.surface_preference} onValueChange={v => updateForm('surface_preference', v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="implant-surface-select"><SelectValue placeholder="Cualquiera" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any_surface">Cualquiera</SelectItem>
                  <SelectItem value="smooth">Lisa</SelectItem>
                  <SelectItem value="textured">Texturizada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Cascade: Modelo / Línea de producto — appears only when a brand is selected
              and there are product_lines for that brand in the catalog. */}
          {form.brand_preference && form.brand_preference !== 'all_brands' && productLineList.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs font-medium">Modelo / Línea {form.brand_preference}</Label>
                <Select
                  value={form.product_line_preference}
                  onValueChange={v => updateForm('product_line_preference', v)}
                >
                  <SelectTrigger className="h-9 text-sm" data-testid="implant-product-line-select">
                    <SelectValue placeholder="Todos los modelos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_lines">Todos los modelos</SelectItem>
                    {productLineList.map(line => (
                      <SelectItem key={line} value={line}>{line}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <p className="text-[11px] text-gray-500">
                  {productLineList.length} modelo{productLineList.length === 1 ? '' : 's'} en catálogo
                </p>
              </div>
            </div>
          )}

          {/* Live Top-3 preview (debounced, no AI) */}
          {(preview?.top3?.length > 0 || previewLoading) && (
            <div
              className="rounded-lg border border-brand-medium/70 bg-white/70 px-3 py-2 space-y-1.5"
              data-testid="implant-quick-preview"
            >
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-medium text-brand-navy flex items-center gap-1">
                  <Sparkles size={11} /> Vista previa instantánea
                  {previewLoading && <Loader2 size={10} className="animate-spin" />}
                </span>
                {preview?.total_candidates > 0 && (
                  <span className="text-brand-navy/70">
                    {preview.total_candidates} candidatos en catálogo
                  </span>
                )}
              </div>
              {preview?.top3?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                  {preview.top3.map((c, i) => (
                    <div
                      key={`${c.reference}-${i}`}
                      className="rounded border border-indigo-100 bg-brand-soft/40/40 px-2 py-1.5 text-[11px] leading-tight"
                      data-testid={`implant-preview-card-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-brand-navy truncate">
                          {c.brand}
                        </span>
                        <Badge className={`${PROFILE_COLORS[c.profile] || 'bg-slate-100 text-slate-600'} text-[9px] px-1 py-0`}>
                          {PROFILE_LABELS[c.profile] || c.profile || '—'}
                        </Badge>
                      </div>
                      <div className="text-slate-600 truncate">{c.product_line}</div>
                      <div className="text-slate-700 font-mono text-[10px]">{c.reference}</div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-600 mt-0.5">
                        <span><b>{c.volume_cc}</b>cc</span>
                        <span>Ø{c.base_width_mm}mm</span>
                        {c.projection_mm && <span>Pr{c.projection_mm}mm</span>}
                        {c.surface && (
                          <span className="ml-auto text-slate-500 italic">{c.surface}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                !previewLoading && (
                  <div className="text-[11px] text-slate-500 italic">
                    Sin candidatos para los filtros actuales.
                  </div>
                )
              )}
              <div className="text-[10px] text-slate-500 italic">
                Sugerencia rápida sin IA. Pulsa "Calcular" para análisis completo con justificación clínica.
              </div>
            </div>
          )}

          {/* Photos section */}
          <div className="space-y-2">
            <Label className="text-xs font-medium flex items-center gap-1">
              <Camera size={12} /> Fotos del paciente (opcionales — mejoran la recomendación)
              {galleryLoading && <Loader2 size={11} className="animate-spin text-brand-navy" />}
              {patientId && galleryPhotos.length > 0 && (
                <Badge className="bg-brand-soft/40 text-brand-navy text-[9px] ml-1">
                  {selectedGalleryIds.size}/{galleryPhotos.length} de galería
                </Badge>
              )}
            </Label>
            
            <div className="flex flex-wrap gap-2">
              {/* Existing reference photos */}
              {referencePhotos.map((photo, i) => (
                <div key={photo.id || i} className="relative w-16 h-16 rounded border overflow-hidden group">
                  <img src={photo.url} alt={photo.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <span className="text-[8px] text-white">Referencia</span>
                  </div>
                  <div className="absolute top-0 right-0 bg-brand-navy text-white text-[7px] px-1 rounded-bl">REF</div>
                </div>
              ))}

              {/* Auto-loaded gallery photos (from MedicalGallery) — toggleable */}
              {galleryPhotos.map((photo) => {
                const selected = selectedGalleryIds.has(photo.id);
                const url = photo.url || `/api/medical-files/${photo.id}/view?token=${localStorage.getItem('token')}`;
                return (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => toggleGalleryPhoto(photo.id)}
                    className={`relative w-16 h-16 rounded border-2 overflow-hidden transition-all ${
                      selected ? 'border-indigo-500 ring-2 ring-indigo-300' : 'border-gray-200 opacity-60 hover:opacity-100'
                    }`}
                    title={selected ? 'Click para deseleccionar' : 'Click para incluir en el análisis'}
                  >
                    <img src={url} alt={photo.original_filename || 'foto'} className="w-full h-full object-cover" />
                    {selected && (
                      <div className="absolute top-0 right-0 bg-brand-navy text-white text-[7px] px-1 rounded-bl font-bold">✓</div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[7px] py-0.5 text-center">
                      Galería
                    </div>
                  </button>
                );
              })}

              {/* Extra uploaded photos */}
              {extraPhotos.map((photo, i) => (
                <div key={i} className="relative w-16 h-16 rounded border overflow-hidden group">
                  <img src={photo.preview} alt={photo.name} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeExtraPhoto(i)}
                    className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={10} />
                  </button>
                </div>
              ))}

              {/* Add photo button */}
              <button type="button" onClick={() => fileInputRef.current?.click()}
                className="w-16 h-16 rounded border-2 border-dashed border-brand-medium flex flex-col items-center justify-center text-brand-medium hover:bg-brand-soft/40 hover:border-brand-medium transition-colors"
                data-testid="implant-add-photo-btn">
                <Camera size={16} />
                <span className="text-[8px] mt-0.5">Añadir</span>
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
                onChange={handleAddPhotos} />
            </div>

            {totalPhotos > 0 && (
              <p className="text-[10px] text-brand-navy">
                <ImageIcon size={10} className="inline mr-1" />
                {totalPhotos} foto{totalPhotos !== 1 ? 's' : ''} se enviarán a la IA para análisis visual
              </p>
            )}
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-xs font-medium">Notas adicionales</Label>
              <Input placeholder="Ej: Paciente prefiere resultado natural, piel fina..."
                value={form.additional_notes} onChange={e => updateForm('additional_notes', e.target.value)}
                className="h-9 text-sm" />
            </div>
            <Button type="button" onClick={handleCalculate} disabled={loading || !form.base_width_mm}
              className="bg-brand-navy hover:bg-brand-navy h-9 gap-2" data-testid="implant-calculate-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {loading ? 'Analizando...' : totalPhotos > 0 ? 'Calcular con Visión' : 'Calcular'}
            </Button>
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-4 mt-3" data-testid="implant-results">
              {/* Deterministic TOP 3 - Always shows real catalog data */}
              {result.top3_primary && result.top3_primary.length > 0 && (
                <div className="bg-white border-2 border-brand-medium rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-brand-navy flex items-center gap-2 mb-3">
                    <Search size={14} className="text-brand-navy" />
                    TOP 3 Recomendaciones ({result.top3_primary[0]?.brand || 'Catálogo'})
                  </h4>
                  <div className="space-y-3">
                    {result.top3_primary.slice(0, 3).map((imp, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-brand-soft/40/50 border border-indigo-100">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-navy text-white text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-brand-navy">{imp.brand} {imp.product_line}</p>
                          <p className="font-mono text-xs font-bold text-brand-navy mt-0.5">Ref: {imp.reference}</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-600">
                            <span><strong>{imp.volume_cc}</strong>cc</span>
                            <span>Base: <strong>{imp.base_width_mm}</strong>mm</span>
                            <span>Proy: <strong>{imp.projection_mm}</strong>mm</span>
                            {imp.height_mm && <span>Alt: <strong>{imp.height_mm}</strong>mm</span>}
                            <Badge className={`text-[9px] px-1.5 ${PROFILE_COLORS[imp.profile] || 'bg-gray-100'}`}>
                              {PROFILE_LABELS[imp.profile] || imp.profile}
                            </Badge>
                            <Badge variant="outline" className="text-[9px]">
                              {imp.type === 'round' ? 'Redonda' : 'Anatómica'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Alternative brand suggestions */}
              {result.top3_alt && result.top3_alt.length > 0 && (() => {
                const byBrand = {};
                result.top3_alt.forEach(imp => {
                  if (!byBrand[imp.brand]) byBrand[imp.brand] = [];
                  byBrand[imp.brand].push(imp);
                });
                return (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-2 mb-3">
                      <Search size={12} />
                      Sugerencias de otras marcas
                    </h4>
                    <div className="space-y-4">
                      {Object.entries(byBrand).map(([brand, imps]) => (
                        <div key={brand}>
                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">{brand}</p>
                          <div className="space-y-2">
                            {imps.slice(0, 3).map((imp, idx) => (
                              <div key={idx} className="flex items-start gap-2 p-2 rounded bg-gray-50 border border-gray-100">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-400 text-white text-[10px] font-bold flex items-center justify-center">{idx + 1}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-xs text-gray-800">{imp.brand} {imp.product_line} — <span className="font-mono font-bold">Ref: {imp.reference}</span></p>
                                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-gray-500">
                                    <span>{imp.volume_cc}cc</span>
                                    <span>Base: {imp.base_width_mm}mm</span>
                                    <span>Proy: {imp.projection_mm}mm</span>
                                    <span>{PROFILE_LABELS[imp.profile] || imp.profile}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* AI Analysis */}
              {result.ai_analysis && (
                <div className="bg-white border border-indigo-100 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-brand-navy flex items-center gap-2 mb-3">
                    <Sparkles size={14} className="text-brand-navy" />
                    Resultado del Análisis {result.photos_analyzed > 0 && (
                      <Badge className="bg-green-100 text-green-700 text-[9px]">
                        <Camera size={9} className="mr-1" />{result.photos_analyzed} foto{result.photos_analyzed !== 1 ? 's' : ''} analizadas
                      </Badge>
                    )}
                  </h4>
                  <div className="text-sm text-gray-700 leading-relaxed space-y-1">
                    {result.ai_analysis.split('\n').map((line, i) => {
                      const trimmed = line.trim();
                      if (!trimmed) return <div key={i} className="h-2" />;
                      if (/^(TOP \d|RANGO|SUGERENCIAS|ANÁLISIS VISUAL|JUSTIFICACIÓN|PERFIL RECOMENDADO|CONSIDERACIONES|ADVERTENCIAS|ALTERNATIVAS)/i.test(trimmed)) {
                        return <p key={i} className="font-bold text-brand-navy mt-3 mb-1 text-sm">{trimmed}</p>;
                      }
                      const parts = trimmed.split(/(\*\*[^*]+\*\*)/g);
                      const rendered = parts.map((part, j) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={j} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
                        }
                        return part;
                      });
                      if (/^\d+\./.test(trimmed)) return <p key={i} className="ml-1 mt-2 font-medium">{rendered}</p>;
                      if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) return <p key={i} className="ml-4 text-gray-600">{rendered}</p>;
                      return <p key={i}>{rendered}</p>;
                    })}
                  </div>
                </div>
              )}

              {/* Export PDF + Save to clinical note */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                {onNoteSnapshotChange && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-1.5 text-xs text-brand-navy cursor-pointer select-none" data-testid="include-prosthesis-in-report-label">
                      <input
                        type="checkbox"
                        checked={!!noteSnapshot?.include_in_report}
                        onChange={(e) => {
                          const top = (result?.top3_primary?.[0]) || (result?.implants?.[0]) || null;
                          const snap = {
                            base_width_mm: form.base_width_mm ? parseFloat(form.base_width_mm) : null,
                            height_mm: form.height_mm ? parseFloat(form.height_mm) : null,
                            chest_circumference_cm: form.chest_circumference_cm ? parseFloat(form.chest_circumference_cm) : null,
                            procedure_type: form.procedure_type,
                            brand_preference: form.brand_preference || null,
                            profile_preference: form.profile_preference || null,
                            surface_preference: form.surface_preference || null,
                            top_pick: top ? `${top.brand || ''} ${top.line || ''} ${top.ref || ''} ${top.volume_cc ? top.volume_cc + 'cc' : ''}`.trim() : null,
                            recommendation: result?.recommendation_summary || result?.recommendation || null,
                            notes: form.additional_notes || null,
                          };
                          onNoteSnapshotChange({
                            snapshot: e.target.checked ? snap : (noteSnapshot?.snapshot || snap),
                            include_in_report: e.target.checked,
                          });
                        }}
                        className="accent-indigo-600"
                        data-testid="include-prosthesis-in-report-checkbox"
                      />
                      Incluir medidas en informe médico
                    </label>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 text-[11px] text-brand-navy hover:bg-brand-soft/40 px-2"
                      onClick={() => {
                        const top = (result?.top3_primary?.[0]) || (result?.implants?.[0]) || null;
                        const snap = {
                          base_width_mm: form.base_width_mm ? parseFloat(form.base_width_mm) : null,
                          height_mm: form.height_mm ? parseFloat(form.height_mm) : null,
                          chest_circumference_cm: form.chest_circumference_cm ? parseFloat(form.chest_circumference_cm) : null,
                          procedure_type: form.procedure_type,
                          brand_preference: form.brand_preference || null,
                          profile_preference: form.profile_preference || null,
                          surface_preference: form.surface_preference || null,
                          top_pick: top ? `${top.brand || ''} ${top.line || ''} ${top.ref || ''} ${top.volume_cc ? top.volume_cc + 'cc' : ''}`.trim() : null,
                          recommendation: result?.recommendation_summary || result?.recommendation || null,
                          notes: form.additional_notes || null,
                        };
                        onNoteSnapshotChange({ snapshot: snap, include_in_report: !!noteSnapshot?.include_in_report });
                        toast.success('Medidas guardadas en la nota');
                      }}
                      data-testid="save-prosthesis-to-note-btn"
                    >
                      Guardar en nota
                    </Button>
                    {noteSnapshot?.snapshot && (
                      <span className="text-[10px] text-brand-navy italic">
                        Guardado{!noteSnapshot.include_in_report && ' (no se incluye en informe)'}
                      </span>
                    )}
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setExportOpen(true)}
                  className="text-brand-navy border-brand-medium hover:bg-brand-soft/40 gap-2 ml-auto"
                  data-testid="implant-export-pdf-btn"
                >
                  <FileDown size={14} />
                  Exportar a PDF
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (window.confirm('¿Descartar este borrador y empezar de nuevo? Se perderán todos los cambios actuales.')) {
                      clearDraft();
                      toast.success('Borrador descartado');
                    }
                  }}
                  className="text-gray-500 hover:text-gray-700 gap-1.5 text-xs"
                  data-testid="implant-discard-draft-btn"
                  title="Descartar borrador y reiniciar el formulario"
                >
                  <X size={12} />
                  Empezar de nuevo
                </Button>
              </div>

              {/* Implant table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 border-b">
                  <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                    <Search size={12} />
                    Prótesis compatibles ({result.total_candidates} encontradas, top {result.implants.length})
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b text-gray-600">
                        <th className="px-3 py-2 text-left font-medium">Marca</th>
                        <th className="px-3 py-2 text-left font-medium">Línea</th>
                        <th className="px-3 py-2 text-left font-medium">Ref</th>
                        <th className="px-2 py-2 text-center font-medium">Vol (cc)</th>
                        <th className="px-2 py-2 text-center font-medium">Base (mm)</th>
                        <th className="px-2 py-2 text-center font-medium">Proy (mm)</th>
                        <th className="px-2 py-2 text-center font-medium">Alt (mm)</th>
                        <th className="px-2 py-2 text-left font-medium">Perfil</th>
                        <th className="px-2 py-2 text-center font-medium">Tipo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.implants.map((imp, idx) => (
                        <tr key={idx} className={`border-b hover:bg-brand-soft/40/30 ${idx < 3 ? 'bg-brand-soft/40/20' : ''}`}>
                          <td className="px-3 py-2 font-medium">{imp.brand}</td>
                          <td className="px-3 py-2 text-gray-600">{imp.product_line}</td>
                          <td className="px-3 py-2 font-mono text-[10px] font-semibold">{imp.reference}</td>
                          <td className="px-2 py-2 text-center font-semibold">{imp.volume_cc}</td>
                          <td className="px-2 py-2 text-center">{imp.base_width_mm}</td>
                          <td className="px-2 py-2 text-center">{imp.projection_mm}</td>
                          <td className="px-2 py-2 text-center">{imp.height_mm || '-'}</td>
                          <td className="px-2 py-2">
                            <Badge className={`text-[9px] px-1.5 ${PROFILE_COLORS[imp.profile] || 'bg-gray-100'}`}>
                              {PROFILE_LABELS[imp.profile] || imp.profile}
                            </Badge>
                          </td>
                          <td className="px-2 py-2 text-center">
                            <Badge variant="outline" className="text-[9px]">
                              {imp.type === 'round' ? 'Redonda' : 'Anatómica'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Export Dialog */}
      {result && (
        <ImplantExportDialog
          open={exportOpen}
          onOpenChange={setExportOpen}
          implants={result.implants || []}
          top3Primary={result.top3_primary || []}
          top3Alt={result.top3_alt || []}
          queryParams={result.query_params || {}}
          patientId={patientId}
          patientName={patientName}
        />
      )}
    </div>
  );
}
